// Reference loft solver — packet → welded 3D vertices + closure QA.
// z = level.eaveHeightM + perpDistance(vertex, eaveEdgeLine) * tan(pitch)
// Pure: no THREE, no DOM. Byte-for-byte the same maths as the § H reference impl.

import type { RoofvizPacket, RoofPlane, RoofLine, RoofLevel, XY } from "./contract";

const D2R = Math.PI / 180;
const key = (p: XY): string => p[0].toFixed(4) + "," + p[1].toFixed(4);

export interface SolvedVertex {
  x: number;
  y: number;
  z: number;
  k: string;
}

export interface SolvedPlane extends RoofPlane {
  verts: SolvedVertex[];
  eaveA: XY;
  eu: { x: number; y: number };
  nrm: { x: number; y: number };
  planAreaM2: number;
  trueAreaM2: number;
  level: RoofLevel;
}

export interface SolvedLine extends RoofLine {
  pts3: { x: number; y: number; z: number }[];
  lengthM: number;
}

export interface SolveTotals {
  planAreaM2: number;
  surfaceAreaM2: number;
  ridgeM: number;
  hipM: number;
  valleyM: number;
  eaveM: number;
  storeys: number;
}

export interface SolveResult {
  planes: SolvedPlane[];
  lines: SolvedLine[];
  weld: Map<string, number>;
  maxClosureDevM: number;
  totals: SolveTotals;
}

export function solvePacket(pkt: RoofvizPacket): SolveResult {
  const levels: Record<string, RoofLevel> = Object.fromEntries(pkt.levels.map((l) => [l.id, l]));
  const cand = new Map<string, number[]>(); // vertexKey -> [z, z, ...]

  const planes: SolvedPlane[] = pkt.planes.map((pl) => {
    const lvl = levels[pl.levelId];
    const [ia, ib] = pl.eaveEdge;
    const A = pl.ring[ia];
    const B = pl.ring[ib];
    const ex = B[0] - A[0];
    const ey = B[1] - A[1];
    const eLen = Math.hypot(ex, ey);
    const nx = -ey / eLen;
    const ny = ex / eLen; // unit normal to eave line (in plan)
    const t = Math.tan(pl.pitchDeg * D2R);
    const verts: SolvedVertex[] = pl.ring.map((p) => {
      const d = Math.abs((p[0] - A[0]) * nx + (p[1] - A[1]) * ny); // perp distance to eave line
      const z = lvl.eaveHeightM + d * t;
      const k = key(p);
      if (!cand.has(k)) cand.set(k, []);
      cand.get(k)!.push(z);
      return { x: p[0], y: p[1], z, k };
    });
    const eu = { x: ex / eLen, y: ey / eLen }; // along eave (u); v runs up-slope
    const planAreaM2 = Math.abs(
      pl.ring.reduce((s, p, i) => {
        const q = pl.ring[(i + 1) % pl.ring.length];
        return s + (p[0] * q[1] - q[0] * p[1]);
      }, 0) / 2,
    );
    return {
      ...pl,
      verts,
      eaveA: A,
      eu,
      nrm: { x: nx, y: ny },
      planAreaM2,
      trueAreaM2: planAreaM2 / Math.cos(pl.pitchDeg * D2R),
      level: lvl,
    };
  });

  // weld: final z per shared vertex = mean of candidates; QA = max spread across candidates
  const weld = new Map<string, number>();
  let maxDev = 0;
  cand.forEach((zs, k) => {
    const mean = zs.reduce((a, b) => a + b, 0) / zs.length;
    maxDev = Math.max(maxDev, Math.max(...zs) - Math.min(...zs));
    weld.set(k, mean);
  });
  planes.forEach((pl) => pl.verts.forEach((v) => (v.z = weld.get(v.k)!)));

  // true 3D line lengths from welded vertices
  const lines: SolvedLine[] = pkt.lines.map((ln) => {
    let len3 = 0;
    const pts3: { x: number; y: number; z: number }[] = [];
    ln.polyline.forEach((p, i) => {
      const z = weld.get(key(p)) ?? 0;
      pts3.push({ x: p[0], y: p[1], z });
      if (i > 0) {
        const q = pts3[i - 1];
        len3 += Math.hypot(p[0] - q.x, p[1] - q.y, z - q.z);
      }
    });
    return { ...ln, pts3, lengthM: len3 };
  });

  const sum = (cls: string): number =>
    lines.filter((l) => l.class === cls).reduce((s, l) => s + l.lengthM, 0);

  const totals: SolveTotals = {
    planAreaM2: planes.reduce((s, p) => s + p.planAreaM2, 0),
    surfaceAreaM2: planes.reduce((s, p) => s + p.trueAreaM2, 0),
    ridgeM: sum("ridge"),
    hipM: sum("hip"),
    valleyM: sum("valley"),
    eaveM: sum("eave"),
    storeys: pkt.aggregates.storeys,
  };

  return { planes, lines, weld, maxClosureDevM: maxDev, totals };
}
